import {
  getValueByClassification,
  normalizeFieldToArray
} from "@thegetty/linkedart.js";
import internal from "@/data/internal-ontologies";
import {
  getManifestObjects,
  getManifestUrls as getSharedManifestUrls,
  IIIF_V2,
  extractImageUuid
} from "@thegetty-private/linked-data.js";
import * as configHelpers from "@/helpers/configHelper";

const IDENTIFIED_BY = "identified_by";

/**
 * Gets the Digital Object Repository ID from the data
 * @param {object} object - the JSON-LD object
 *
 * @returns {Number} the DOR ID
 */
export function getDORID(object) {
  const dor = _getID(object, internal.DOR_ID);
  if (!dor) return undefined;
  return parseInt(dor);
}

/**
 * Gets the LOD slug identifier from a JSON-LD object
 * @param {object} object - the JSON-LD object
 *
 * @returns {string} the slug ID
 */
export function getSlugID(object) {
  const slug = _getID(object, internal.SLUG_ID);
  if (!slug) return undefined;
  // get the last part of the full slug
  let slug_parts = slug.split("/");
  return slug_parts[slug_parts.length - 1];
}

/**
 * Gets The Museum System ID from the data
 * @param {object} object - the JSON-LD object
 *
 * @returns {Number} the TMS ID
 */
export function getTMSID(object) {
  const tms = _getID(object, internal.TMS_ID);
  if (!tms) return undefined;
  return parseInt(tms);
}

/**
 * Gets The UUID from the data
 * @param {object} object - the JSON-LD object
 *
 * @returns {string} the UUID ID
 */
export function getUUID(object) {
  const uuid = _getID(object, internal.UUID);
  return uuid;
}

/**
 * Gets The ID from the data
 * @param {object} object - the JSON-LD object
 * @param {string} id_type - the classification of identifier to find
 *
 * @returns {string} the UUID ID
 */
function _getID(object, id_type) {
  return getValueByClassification(object[IDENTIFIED_BY], id_type);
}

/**
 * removes empty values from an object
 *
 * @param {object} object - the object
 *
 * @returns {object} the cleaned object
 */
export function stripEmptyObjectParts(object) {
  if (object == undefined) {
    return {};
  }
  // remove any unmapped values
  Object.keys(object).forEach((key) => {
    if (
      object[key] == undefined ||
      (Array.isArray(object[key]) && object[key].length == 0)
    ) {
      delete object[key];
    }
  });
  return object;
}

/**
 * Filters an array to remove undefined values
 *
 * @param {array} array - an array
 *
 * @returns {array} an array of defined values
 */
export function filterUndefined(array) {
  if (array == undefined) {
    return [];
  }
  return array.filter((v) => v != undefined);
}

/**
 * returns additional internal ids (DOR ID, TMS ID)
 *
 * @param {Object} object
 * @returns  {Object} other ids object
 */
export function getOtherIds(object) {
  if (object == undefined) {
    return;
  }

  let otherIds = Object.entries({
    tms_id: getTMSID(object),
    dor_id: getDORID(object)
  })
    .filter(([_id, value]) => value != undefined)
    .reduce((obj, [id, value]) => {
      obj[id] = value;
      return obj;
    }, {});

  return otherIds;
}

/**
 * Gets the manifest URL from the object
 * @param {object} object - the JSON-LD Object
 *
 * @returns {string} the manifest URL
 */
export function getManifestUrls(object) {
  // check how many manifests there are & if we should be using custom manifests
  let options = {};

  if (configHelpers.useCustomIIIFManifest()) {
    options.excludeCustom = false;
  }

  let manifests = getManifestObjects(object, options);

  if (manifests.length == 0) {
    return undefined;
  }

  // if there is only one manifest, use by default
  if (manifests.length == 1) {
    return getSharedManifestUrls(object, options)[0].replace(".json", "");
  }

  let url;

  // if there are multiple manifests & there is a custom manifest
  // (& feature flag on + canvas panel is on), use that one
  if (configHelpers.useCustomIIIFManifest()) {
    let customManifest = manifests.find((manifest) => {
      if (manifest.custom == true) {
        return manifest;
      }
    });

    if (customManifest) {
      url = customManifest.url;
    }
  } else {
    // if feature flag / canvas panel not on, default back to "normal" manifest
    let manifest = manifests.find((manifest) => {
      if (manifest.custom == false) {
        return manifest;
      }
    });

    if (manifest) {
      url = manifest.url;
    }
  }

  if (!url) {
    return undefined;
  }

  url = url.replace(".json", "");

  return url;
}

/**
 * @TODO Temporary check for Canvas Panel transition - remove when no longer needed
 * Checks if manifest objects contain a custom manifest (aka Bayard)
 *
 * @param {object} object - the JSON-LD object
 *
 * @returns {boolean}
 */
export function containsCustomManifest(object) {
  // get all manifest objects available
  let manifests = getManifestObjects(object, {
    excludeCustom: false
  });

  let customManifest = manifests.find((manifest) => {
    if (manifest.custom == true) {
      return manifest;
    }
  });

  if (customManifest) {
    return true;
  }

  return false;
}

/**
 * Returns technical data component metadata fields object
 * @param {Object} object
 * @param {Object} translate translate function
 * @param {Boolean} includeIiif whether to include iiif fields
 *
 * @returns {object} technicalMetadataFields
 */
export function getTechnicalMetadataFields(
  object,
  translate = undefined,
  includeIiif = false,
  baseLocaleKey = "m-technical-data",
  lang = "en"
) {
  if (object == undefined) {
    return;
  }

  const indexedId = object._id ? object._id : object.id;
  // date format options
  const options = { year: "numeric", month: "long", day: "numeric" };

  // get and format other ids metadata
  let otherIdsValues = getOtherIds(object);
  let other_ids = {};
  Object.keys(otherIdsValues).forEach((k) => {
    other_ids[k] = {
      name: String(otherIdsValues[k]),
      label: translate ? translate(`${baseLocaleKey}.${k}`) : k,
      text: translate ? translate(`${baseLocaleKey}.${k}_text`) : k
    };
  });

  // get and format iiif metadata
  let iiif;
  if (includeIiif) {
    let manifests = getManifestObjects(object, {
      excludeCustom: false
    });

    // use v2 manifest for now for mirador viewer
    let v2 = manifests.filter((manifest) => {
      if (manifest.custom == false && manifest.format == IIIF_V2) {
        return manifest;
      }
    })[0];

    iiif = {
      manifests: manifests,
      viewer_url: configHelpers.getMiradorUrl(v2.url)
    };
  }

  // get and format last updated date
  let date = new Date(object.last_updated);
  let lastUpdated =
    date == "Invalid Date"
      ? undefined
      : new Intl.DateTimeFormat(lang, options).format(date);

  let out = {
    indexed_id: indexedId,
    other_ids: other_ids,
    iiif: iiif,
    sparql_api: getSparqlUIUrl(indexedId),
    id_relationships: configHelpers.getIdManagerLinksUrl(indexedId),
    last_updated: lastUpdated
  };
  return out;
}

/**
 * Returns returns example sparql ui query based on object
 * @param {Object} indexedId
 *
 * @returns {String} sparql ui endpoint with example query url
 */
export function getSparqlUIUrl(indexedId) {
  const endpoint = configHelpers.getIdManagerDataUrl();
  let query = `PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
  PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
  PREFIX crm: <http://www.cidoc-crm.org/cidoc-crm/>
  PREFIX skos: <http://www.w3.org/2004/02/skos/core#>
  PREFIX la: <https://linked.art/ns/terms/>
  PREFIX aat: <http://vocab.getty.edu/aat/>
  PREFIX getty: <http://data.getty.edu/local/>
  
  SELECT * WHERE {
    <${indexedId}> ?pred ?obj
  }`;

  query = encodeURIComponent(query);
  return `${endpoint}sparql-ui#query=${query}`;
}

/**
 * Extracts the thumbnail IIIF Image UUID from a manifest to use as
 * the image for the Sticky Box
 *
 * @param {object} - manifest
 *
 * @returns {string | undefined} - the IIIF Image UUID
 */
export function getManifestThumbnail(manifest) {
  const thumbnail = normalizeFieldToArray(manifest, "thumbnail");

  if (thumbnail.length == 0) {
    return undefined;
  }

  return extractImageUuid(thumbnail[0]["@id"] || thumbnail[0].id);
}
/**
 *
 * Removes empty (null / undefined entries)
 *
 * @param {obj}
 *
 * @returns {obj}
 *
 */
export function removeEmpty(obj) {
  if (Array.isArray(obj)) {
    return obj
      .map((v) => (v && typeof v === "object" ? removeEmpty(v) : v))
      .filter((v) => !(v == null || v == undefined));
  }
  return Object.entries(obj)
    .map(([k, v]) => [k, v && typeof v === "object" ? removeEmpty(v) : v])
    .reduce(
      (a, [k, v]) => (v == null || v == undefined ? a : ((a[k] = v), a)),
      {}
    );
}
